import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ScrollText, Scale, Info, Globe2, ChevronLeft, ChevronRight, FileText, AlertCircle, BookOpen, Users, Lock } from "lucide-react";

const SECTION_ICONS = [
  <Info className="w-8 h-8" />,
  <ScrollText className="w-8 h-8" />,
  <Scale className="w-8 h-8" />,
  <Scale className="w-8 h-8" />,
  <Scale className="w-8 h-8" />,
  <Globe2 className="w-8 h-8" />,
  <Globe2 className="w-8 h-8" />,
  <Scale className="w-8 h-8" />,
  <Info className="w-8 h-8" />,
  <Info className="w-8 h-8" />,
  <Info className="w-8 h-8" />,
  <Scale className="w-8 h-8" />,
  <Scale className="w-8 h-8" />,
  <Globe2 className="w-8 h-8" />,
  <Info className="w-8 h-8" />,
];

export default function Terms() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [activeSection, setActiveSection] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const sections = t("terms.sections", { returnObjects: true }).map((s, i) => ({
    ...s,
    icon: SECTION_ICONS[i],
  }));

  const filteredSections = sections.filter(section =>
    section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    section.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    section.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const nextSection = () => {
    setActiveSection((prev) => (prev + 1) % filteredSections.length);
  };

  const prevSection = () => {
    setActiveSection((prev) => (prev - 1 + filteredSections.length) % filteredSections.length);
  };

  const importanceHigh = t("terms.importance.high");
  const importanceMedium = t("terms.importance.medium");

  const getImportanceColor = (importance) => {
    if (importance === importanceHigh) return "bg-error/10 text-error";
    if (importance === importanceMedium) return "bg-warning/10 text-warning";
    if (importance) return "bg-success/10 text-success";
    return "bg-grey/10 text-grey";
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: 100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle text-dark-grey pt-32 pb-20 px-6 sm:px-10 md:px-20">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-6xl mx-auto text-center mb-12"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5, type: "spring" }}
          className="w-24 h-24 bg-gradient-to-br from-primary to-medium-blue rounded-xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-primary/30"
        >
          <BookOpen className="w-12 h-12 text-white" />
        </motion.div>
        <h1 className="text-4xl md:text-6xl font-black text-dark-grey mb-6 leading-tight">
          {t("terms.title")}
        </h1>
        <div className="w-32 h-2 bg-gradient-to-l from-primary to-medium-blue mx-auto mb-6 rounded-full"></div>
        <p className="text-xl text-grey max-w-3xl mx-auto leading-relaxed mb-8">
          {t("terms.subtitle")}
        </p>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="max-w-2xl mx-auto relative"
        >
          <input
            type="text"
            placeholder={t("terms.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-6 py-4 ps-14 bg-white shadow-card rounded-xl border border-grey/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all duration-300 [text-align:start]"
          />
          <FileText className="w-6 h-6 text-grey absolute start-5 top-1/2 transform -translate-y-1/2" />
        </motion.div>
      </motion.div>

      {/* Results Count */}
      {searchTerm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="max-w-6xl mx-auto [text-align:start] mb-8"
        >
          <p className="text-primary font-semibold">
            {t("terms.resultsFound", { count: filteredSections.length, term: searchTerm })}
          </p>
        </motion.div>
      )}

      {/* Desktop Grid Layout */}
      <div className="hidden lg:block max-w-7xl mx-auto">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 gap-8"
        >
          {(searchTerm ? filteredSections : sections).map((section, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white rounded-xl shadow-card hover:shadow-xl transition-all duration-500 border border-transparent hover:border-primary/20 overflow-hidden"
            >
              <div className="flex items-start gap-8 p-8">
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="w-20 h-20 bg-gradient-to-br from-primary to-medium-blue rounded-xl flex items-center justify-center shadow-lg shadow-primary/30 text-white">
                      {section.icon}
                    </div>
                    <div className="absolute -bottom-2 -start-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {index + 1}
                    </div>
                  </div>
                </div>

                <div className="flex-grow [text-align:start]">
                  <h2 className="text-3xl font-bold text-dark-grey mb-3">
                    {section.title}
                  </h2>
                  <div className="flex flex-wrap gap-2 justify-start mb-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getImportanceColor(section.importance)}`}>
                      {section.importance}
                    </span>
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                      {section.category}
                    </span>
                  </div>

                  <p className="text-grey leading-loose text-lg whitespace-pre-line mb-4">
                    {section.content}
                  </p>

                  <div className="flex flex-row-reverse items-center gap-4 mt-6">
                    <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                    <span className="text-sm text-grey">{t("terms.lastUpdated")}: {new Date().getFullYear()}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Mobile Carousel Layout */}
      <div className="lg:hidden max-w-2xl mx-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isRTL ? -50 : 50 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-xl shadow-card overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-medium-blue rounded-xl flex items-center justify-center shadow-lg shadow-primary/30 text-white">
                  {filteredSections[activeSection]?.icon}
                </div>
                <div className="[text-align:end]">
                  <span className="text-sm text-grey">
                    {activeSection + 1} / {filteredSections.length}
                  </span>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-dark-grey [text-align:start] mb-4">
                {filteredSections[activeSection]?.title}
              </h2>

              <div className="flex flex-wrap gap-2 justify-end mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getImportanceColor(filteredSections[activeSection]?.importance)}`}>
                  {filteredSections[activeSection]?.importance}
                </span>
                <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  {filteredSections[activeSection]?.category}
                </span>
              </div>

              <p className="text-grey leading-loose [text-align:start] whitespace-pre-line">
                {filteredSections[activeSection]?.content}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Carousel Controls */}
        <div className="flex justify-center items-center gap-6 mt-8">
          <button
            onClick={prevSection}
            className="p-3 bg-white rounded-xl shadow-card hover:shadow-lg transition-all duration-300 hover:bg-light-grey"
          >
            {isRTL ? <ChevronRight className="w-6 h-6 text-primary" /> : <ChevronLeft className="w-6 h-6 text-primary" />}
          </button>

          <div className="flex gap-2">
            {filteredSections.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveSection(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === activeSection ? "bg-primary scale-125" : "bg-primary/20"
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextSection}
            className="p-3 bg-white rounded-xl shadow-card hover:shadow-lg transition-all duration-300 hover:bg-light-grey"
          >
            {isRTL ? <ChevronLeft className="w-6 h-6 text-primary" /> : <ChevronRight className="w-6 h-6 text-primary" />}
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="max-w-6xl mx-auto mt-16 grid grid-cols-2 md:grid-cols-4 gap-6"
      >
        {[
          { icon: <FileText className="w-8 h-8" />, label: t("terms.statLabels.totalTerms"), value: sections.length },
          { icon: <AlertCircle className="w-8 h-8" />, label: t("terms.statLabels.highImportance"), value: sections.filter(s => s.importance === importanceHigh).length },
          { icon: <Users className="w-8 h-8" />, label: t("terms.statLabels.categories"), value: new Set(sections.map(s => s.category)).size },
          { icon: <Lock className="w-8 h-8" />, label: t("terms.statLabels.updated"), value: new Date().getFullYear() },
        ].map((stat, index) => (
          <div key={index} className="bg-white rounded-xl p-6 text-center shadow-card border border-transparent hover:border-primary/20 transition-colors duration-300">
            <div className="text-primary mb-2 flex justify-center">{stat.icon}</div>
            <div className="text-2xl font-bold text-dark-grey mb-1">{stat.value}</div>
            <div className="text-sm text-grey">{stat.label}</div>
          </div>
        ))}
      </motion.div>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="text-center mt-20 text-grey"
      >
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="w-2 h-2 bg-success rounded-full"></div>
          <p className="text-sm">{t("terms.footerNote")}</p>
        </div>
        <p className="text-lg font-semibold text-dark-grey">
          © {new Date().getFullYear()} {t("privacy.subtitleBrand")}. {t("terms.footerRights")}.
        </p>
      </motion.footer>
    </div>
  );
}
